/**
 * Circuit Breaker System
 *
 * This module provides circuit breaker pattern implementation with endpoint-specific
 * failure tracking, automatic circuit opening/closing, and integration with the
 * retry system to prevent cascading failures.
 */

import { getObservability } from '../../../di/container';
import {
  CircuitState,
  DEFAULT_CIRCUIT_BREAKER_CONFIG,
  type CircuitBreakerState,
  type ICircuitBreaker,
  type CircuitBreakerConfig,
  type CircuitBreakerStats,
} from '../../../types/api/circuit-breaker.types';
import {
  CoreNotificationType,
  type CoreNotificationEvent,
} from '../../../types/api/notification.types';
import { ObservabilitySeverity } from '../../../types/observability';
import { dayjs } from '../../../utils/date';

// =============================================================================
// CIRCUIT BREAKER IMPLEMENTATION
// =============================================================================

export class CircuitBreaker implements ICircuitBreaker {
  private circuits: Map<string, CircuitBreakerState> = new Map();
  private config: CircuitBreakerConfig;
  private stats: Map<string, CircuitBreakerStats> = new Map();
  private requestCounts: Map<string, RequestCount[]> = new Map();
  private notificationHandlers: Array<(event: CoreNotificationEvent) => void> =
    [];
  private observability = getObservability();
  private cleanupIntervalId?: ReturnType<typeof setInterval>;

  constructor(config: Partial<CircuitBreakerConfig> = {}) {
    this.config = { ...DEFAULT_CIRCUIT_BREAKER_CONFIG, ...config };
    this.startPeriodicCleanup();
  }

  /**
   * Determines if a request should be allowed for the given endpoint
   */
  shouldAllow(endpoint: string): boolean {
    if (!this.config.enabled) {
      return true;
    }

    const circuit = this.getOrCreateCircuit(endpoint);
    const now = dayjs().valueOf();

    switch (circuit.state) {
      case CircuitState.CLOSED:
        // Circuit is closed, allow all requests
        return true;

      case CircuitState.OPEN:
        // Circuit is open, check if timeout has elapsed
        if (circuit.nextRetryTime && now >= circuit.nextRetryTime) {
          // Transition to half-open
          this.transitionToHalfOpen(endpoint, circuit);
          return true;
        }
        // Circuit is still open, reject request
        // Track blocked request
        this.observability.recordMetric(
          'circuit_breaker.request_blocked',
          1,
          'count',
          { endpoint }
        );
        return false;

      case CircuitState.HALF_OPEN: {
        // In half-open state, allow limited requests to test if service recovered
        const halfOpenRequests = this.countHalfOpenRequests(endpoint);

        // Allow a small number of requests in half-open state
        return halfOpenRequests < this.config.successThreshold;
      }

      default:
        return true;
    }
  }

  /**
   * Records a successful request for the endpoint
   */
  recordSuccess(endpoint: string): void {
    const circuit = this.getOrCreateCircuit(endpoint);
    const stats = this.getOrCreateStats(endpoint);
    const now = dayjs().valueOf();

    // Update stats
    stats.successCount++;
    stats.requestCount++;
    stats.lastSuccessTime = now;

    // Record request timing
    this.recordRequest(endpoint, 'success', now);

    // Update circuit behavior based on current state
    switch (circuit.state) {
      case CircuitState.CLOSED:
        // Reset failure count on success
        circuit.failureCount = 0;
        break;

      case CircuitState.HALF_OPEN:
        // Count successes in half-open state
        if (stats.successCount >= this.config.successThreshold) {
          this.transitionToClosed(endpoint, circuit);
        }
        break;

      case CircuitState.OPEN:
        // Shouldn't happen, but handle gracefully
        console.warn(`Recorded success for open circuit: ${endpoint}`);
        break;
    }
  }

  /**
   * Records a failed request for the endpoint
   */
  recordFailure(endpoint: string): void {
    const circuit = this.getOrCreateCircuit(endpoint);
    const stats = this.getOrCreateStats(endpoint);
    const now = dayjs().valueOf();

    // Update stats
    stats.failureCount++;
    stats.requestCount++;
    stats.lastFailureTime = now;

    // Record request timing
    this.recordRequest(endpoint, 'failure', now);

    // Update circuit behavior based on current state
    switch (circuit.state) {
      case CircuitState.CLOSED:
        circuit.failureCount++;

        // Check if we should open the circuit
        if (this.shouldOpenCircuit(endpoint, circuit)) {
          this.transitionToOpen(endpoint, circuit);
        }
        break;

      case CircuitState.HALF_OPEN:
        // Any failure in half-open state should reopen the circuit
        this.transitionToOpen(endpoint, circuit);
        break;

      case CircuitState.OPEN:
        // Already open, just record the failure
        circuit.failureCount++;
        break;
    }
  }

  /**
   * Gets the current state of a circuit
   */
  getState(endpoint: string): CircuitState {
    const circuit = this.circuits.get(endpoint);
    return circuit?.state || CircuitState.CLOSED;
  }

  /**
   * Gets the full circuit breaker state for an endpoint
   */
  getCircuitState(endpoint: string): CircuitBreakerState {
    return this.getOrCreateCircuit(endpoint);
  }

  /**
   * Gets all circuit breaker states
   */
  getAllCircuitStates(): Map<string, CircuitBreakerState> {
    return new Map(this.circuits);
  }

  /**
   * Manually resets a circuit to closed state
   */
  resetCircuit(endpoint: string): void {
    const circuit = this.getOrCreateCircuit(endpoint);
    const stats = this.getOrCreateStats(endpoint);

    const previousState = circuit.state;

    circuit.state = CircuitState.CLOSED;
    circuit.failureCount = 0;
    circuit.successCount = 0;
    circuit.lastFailureTime = undefined;
    circuit.nextRetryTime = undefined;
    circuit.halfOpenStartTime = undefined;

    stats.failureCount = 0;
    stats.successCount = 0;

    this.notifyStateChange(
      endpoint,
      CircuitState.CLOSED,
      previousState,
      'manual_reset'
    );
  }

  /**
   * Manually forces a circuit to open state
   */
  forceOpen(endpoint: string): void {
    const circuit = this.getOrCreateCircuit(endpoint);

    this.transitionToOpen(endpoint, circuit, 'manual_open');
  }

  /**
   * Manually forces a circuit to close state
   */
  forceClose(endpoint: string): void {
    const circuit = this.getOrCreateCircuit(endpoint);
    const previousState = circuit.state;

    circuit.state = CircuitState.CLOSED;
    circuit.failureCount = 0;
    circuit.nextRetryTime = undefined;
    circuit.halfOpenStartTime = undefined;

    this.notifyStateChange(
      endpoint,
      CircuitState.CLOSED,
      previousState,
      'manual_close'
    );
  }

  /**
   * Gets comprehensive statistics for an endpoint
   */
  getStats(endpoint: string): CircuitBreakerStats {
    const circuit = this.getOrCreateCircuit(endpoint);
    const stats = this.getOrCreateStats(endpoint);

    return {
      ...stats,
      state: circuit.state,
      nextRetryTime: circuit.nextRetryTime,
      halfOpenStartTime: circuit.halfOpenStartTime,
    };
  }

  /**
   * Gets statistics for all endpoints
   */
  getAllStats(): Map<string, CircuitBreakerStats> {
    const allStats = new Map<string, CircuitBreakerStats>();

    this.circuits.forEach((_circuit, endpoint) => {
      allStats.set(endpoint, this.getStats(endpoint));
    });

    return allStats;
  }

  /**
   * Adds a notification handler
   */
  addNotificationHandler(
    handler: (event: CoreNotificationEvent) => void
  ): void {
    this.notificationHandlers.push(handler);
  }

  /**
   * Removes a notification handler
   */
  removeNotificationHandler(
    handler: (event: CoreNotificationEvent) => void
  ): void {
    const index = this.notificationHandlers.indexOf(handler);
    if (index > -1) {
      this.notificationHandlers.splice(index, 1);
    }
  }

  /**
   * Updates circuit breaker configuration
   */
  updateConfig(config: Partial<CircuitBreakerConfig>): void {
    this.config = { ...this.config, ...config };
  }

  // =============================================================================
  // PRIVATE HELPER METHODS
  // =============================================================================

  private getOrCreateCircuit(endpoint: string): CircuitBreakerState {
    if (!this.circuits.has(endpoint)) {
      this.circuits.set(endpoint, {
        endpoint,
        state: CircuitState.CLOSED,
        failureCount: 0,
        successCount: 0,
      });
    }
    return this.circuits.get(endpoint)!;
  }

  private getOrCreateStats(endpoint: string): CircuitBreakerStats {
    if (!this.stats.has(endpoint)) {
      this.stats.set(endpoint, {
        endpoint,
        state: CircuitState.CLOSED,
        failureCount: 0,
        successCount: 0,
        requestCount: 0,
        tripCount: 0,
      });
    }
    return this.stats.get(endpoint)!;
  }

  private shouldOpenCircuit(
    endpoint: string,
    circuit: CircuitBreakerState
  ): boolean {
    // Check if failure threshold is exceeded
    if (circuit.failureCount < this.config.failureThreshold) {
      return false;
    }

    // Check if minimum request volume is met
    const requestCount = this.getRequestCountInWindow(endpoint);
    if (requestCount < this.config.minimumRequestVolume) {
      return false;
    }

    // Calculate failure rate in the monitoring window
    const failureRate = this.getFailureRateInWindow(endpoint);
    const threshold =
      this.config.failureThreshold / this.config.minimumRequestVolume;

    return failureRate >= threshold;
  }

  private transitionToOpen(
    endpoint: string,
    circuit: CircuitBreakerState,
    reason: string = 'failure_threshold'
  ): void {
    const previousState = circuit.state;
    const now = dayjs().valueOf();

    circuit.state = CircuitState.OPEN;
    circuit.lastFailureTime = now;
    circuit.nextRetryTime = now + this.config.timeout;
    circuit.halfOpenStartTime = undefined;

    // Update stats
    const stats = this.getOrCreateStats(endpoint);
    stats.tripCount++;
    stats.state = CircuitState.OPEN;

    // CRITICAL: Circuit opened - service may be down
    this.observability.captureMessage(
      `Circuit breaker OPENED for ${endpoint}`,
      ObservabilitySeverity.ERROR,
      {
        component: 'CircuitBreaker',
        endpoint,
        reason,
        failureCount: circuit.failureCount,
        threshold: this.config.failureThreshold,
        tripCount: stats.tripCount,
        previousState,
      },
      {
        circuit: endpoint,
        state: 'open',
      }
    );

    // Track state transition metric
    this.observability.recordMetric(
      'circuit_breaker.state_change',
      1,
      'count',
      {
        endpoint,
        from: previousState,
        to: 'open',
        reason,
      }
    );

    this.notifyStateChange(endpoint, CircuitState.OPEN, previousState, reason);
  }

  private transitionToHalfOpen(
    endpoint: string,
    circuit: CircuitBreakerState
  ): void {
    const previousState = circuit.state;
    const now = dayjs().valueOf();

    circuit.state = CircuitState.HALF_OPEN;
    circuit.halfOpenStartTime = now;
    circuit.successCount = 0; // Reset success count for half-open testing

    // Update stats
    const stats = this.getOrCreateStats(endpoint);
    stats.state = CircuitState.HALF_OPEN;
    stats.successCount = 0;

    // Calculate downtime duration
    const downtimeDuration = circuit.lastFailureTime
      ? now - circuit.lastFailureTime
      : 0;

    // Track recovery attempt
    this.observability.warning(
      `Circuit breaker entering HALF-OPEN: ${endpoint}`,
      {
        component: 'CircuitBreaker',
        endpoint,
        downtimeDuration,
        previousState,
        tripCount: stats.tripCount,
      }
    );

    // Track state transition metric
    this.observability.recordMetric(
      'circuit_breaker.state_change',
      1,
      'count',
      {
        endpoint,
        from: previousState,
        to: 'half_open',
      }
    );

    this.notifyStateChange(
      endpoint,
      CircuitState.HALF_OPEN,
      previousState,
      'timeout_elapsed'
    );
  }

  private transitionToClosed(
    endpoint: string,
    circuit: CircuitBreakerState
  ): void {
    const previousState = circuit.state;
    const now = dayjs().valueOf();

    circuit.state = CircuitState.CLOSED;
    circuit.failureCount = 0;
    circuit.successCount = 0;
    circuit.lastFailureTime = undefined;
    circuit.nextRetryTime = undefined;
    circuit.halfOpenStartTime = undefined;

    // Update stats
    const stats = this.getOrCreateStats(endpoint);
    stats.state = CircuitState.CLOSED;
    stats.failureCount = 0;

    // Calculate recovery duration if coming from half-open
    const recoveryDuration = circuit.halfOpenStartTime
      ? now - circuit.halfOpenStartTime
      : 0;

    // Track successful recovery
    this.observability.info('Circuit breaker CLOSED - recovery successful', {
      component: 'CircuitBreaker',
      endpoint,
      previousState,
      recoveryDuration,
      tripCount: stats.tripCount,
    });

    // Track state transition metric
    this.observability.recordMetric(
      'circuit_breaker.state_change',
      1,
      'count',
      {
        endpoint,
        from: previousState,
        to: 'closed',
      }
    );

    this.notifyStateChange(
      endpoint,
      CircuitState.CLOSED,
      previousState,
      'success_threshold_met'
    );
  }

  private getRequestCountInWindow(endpoint: string): number {
    const now = dayjs().valueOf();
    const windowStart = now - this.config.monitoringPeriod;
    const requests = this.requestCounts.get(endpoint) || [];

    return requests.filter((req) => req.timestamp >= windowStart).length;
  }

  private getFailureRateInWindow(endpoint: string): number {
    const now = dayjs().valueOf();
    const windowStart = now - this.config.monitoringPeriod;
    const requests = this.requestCounts.get(endpoint) || [];

    const windowRequests = requests.filter(
      (req) => req.timestamp >= windowStart
    );
    if (windowRequests.length === 0) return 0;

    const failures = windowRequests.filter(
      (req) => req.type === 'failure'
    ).length;
    return failures / windowRequests.length;
  }

  private countHalfOpenRequests(endpoint: string): number {
    const circuit = this.circuits.get(endpoint);
    if (!circuit || !circuit.halfOpenStartTime) return 0;

    const requests = this.requestCounts.get(endpoint) || [];
    return requests.filter((req) => req.timestamp >= circuit.halfOpenStartTime!)
      .length;
  }

  private recordRequest(
    endpoint: string,
    type: 'success' | 'failure',
    timestamp: number
  ): void {
    if (!this.requestCounts.has(endpoint)) {
      this.requestCounts.set(endpoint, []);
    }

    const requests = this.requestCounts.get(endpoint)!;
    requests.push({ type, timestamp });

    // Keep only requests within monitoring window
    const cutoff = timestamp - this.config.monitoringPeriod;
    const filtered = requests.filter((req) => req.timestamp >= cutoff);
    this.requestCounts.set(endpoint, filtered);
  }

  private notifyStateChange(
    endpoint: string,
    newState: CircuitState,
    previousState: CircuitState,
    reason: string
  ): void {
    const eventType =
      newState === CircuitState.OPEN
        ? CoreNotificationType.CIRCUIT_BREAKER_OPENED
        : CoreNotificationType.CIRCUIT_BREAKER_CLOSED;

    const event: CoreNotificationEvent = {
      type: eventType,
      requestId: `circuit_${endpoint}`,
      message: `Circuit breaker ${newState} for ${endpoint} (${reason})`,
      timestamp: dayjs().valueOf(),
      data: {
        endpoint,
        newState,
        previousState,
        reason,
      },
    };

    this.notificationHandlers.forEach((handler) => {
      try {
        handler(event);
      } catch (error) {
        console.warn('Circuit breaker notification handler error:', error);
      }
    });
  }

  private startPeriodicCleanup(): void {
    // Clean up old request data periodically
    this.cleanupIntervalId = setInterval(() => {
      const now = dayjs().valueOf();
      const cutoff = now - this.config.monitoringPeriod * 2; // Keep 2x monitoring period

      this.requestCounts.forEach((requests, endpoint) => {
        const filtered = requests.filter((req) => req.timestamp >= cutoff);
        if (filtered.length !== requests.length) {
          this.requestCounts.set(endpoint, filtered);
        }
      });
    }, this.config.monitoringPeriod); // Run cleanup every monitoring period
    // Don't let the cleanup timer keep a Node/test event loop alive (no-op on RN/browser).
    (this.cleanupIntervalId as unknown as { unref?: () => void }).unref?.();
  }

  /**
   * Clears all circuit state (circuits, stats, request history) without
   * stopping the cleanup timer. Intended for test isolation, where a shared
   * breaker would otherwise carry state across cases.
   */
  reset(): void {
    this.circuits.clear();
    this.stats.clear();
    this.requestCounts.clear();
  }

  /**
   * Stops the periodic cleanup timer and releases resources. Call when the
   * owning coordinator is destroyed to avoid leaking the interval.
   */
  destroy(): void {
    if (this.cleanupIntervalId) {
      clearInterval(this.cleanupIntervalId);
      this.cleanupIntervalId = undefined;
    }
    this.circuits.clear();
    this.stats.clear();
    this.requestCounts.clear();
    this.notificationHandlers.length = 0;
  }
}

// =============================================================================
// SUPPORTING INTERFACES
// =============================================================================

interface RequestCount {
  type: 'success' | 'failure';
  timestamp: number;
}

// =============================================================================
// FACTORY FUNCTIONS
// =============================================================================

/**
 * Creates a new circuit breaker instance
 */
export function createCircuitBreaker(
  config?: Partial<CircuitBreakerConfig>
): CircuitBreaker {
  return new CircuitBreaker(config);
}

/**
 * Creates a circuit breaker with default configuration
 */
export function createDefaultCircuitBreaker(): CircuitBreaker {
  return new CircuitBreaker();
}
